import { useMutation } from '@apollo/client/react';
import { type CardDetailsFragment, CardDetailsFragmentDoc } from '@graphql/fragments/cards/core.graphql';
import { UpdateCardFieldDocument } from '@graphql/mutations/cards/field.graphql';
import type { DropResult } from '@hello-pangea/dnd';
import useSingletonContext from '@hooks/use-singleton-context';
import { apolloClient } from '@services/apollo';
import { LexoRank } from 'lexorank';
import { useCallback } from 'react';

interface UseCardDrag {
  handleDragStart: () => void;
  handleDragEnd: (result: DropResult) => void;
}

export function useCardDrag(card?: CardDetailsFragment | null): UseCardDrag {
  const [ctx] = useSingletonContext();
  const [updateCardField] = useMutation(UpdateCardFieldDocument);

  const handleDragStart = useCallback(() => {
    ctx.lock();
  }, [ctx]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) {
        ctx.unlock({
          command: 'make-inactive',
        });

        return;
      }

      const fromIdx = result.source.index;
      const toIdx = result.destination.index;

      const movedToStart = toIdx === 0;
      const movedToEnd = toIdx === (card?.fields?.length ?? 0) - 1;
      const didNotMove = fromIdx === toIdx && result.destination.droppableId === result.source.droppableId;

      if (didNotMove) {
        ctx.unlock();

        return;
      }

      const cardFieldId = result.draggableId;

      const cardFields = [...(card?.fields ?? [])].sort((a, b) => {
        return a.lexorank?.localeCompare(b.lexorank ?? '') ?? 0;
      });

      let newLexoRank: string | undefined;

      switch (true) {
        case movedToStart: {
          const firstCardField = cardFields[0];
          const firstCardFieldLexoRank = firstCardField.lexorank ?? '';

          newLexoRank = LexoRank.parse(firstCardFieldLexoRank).genPrev().format();

          break;
        }
        case movedToEnd: {
          const lastCardField = cardFields[cardFields.length - 1];
          const lastCardFieldLexorank = lastCardField.lexorank ?? '';

          newLexoRank = LexoRank.parse(lastCardFieldLexorank).genNext().format();

          break;
        }
        default: {
          const cardFieldBefore = cardFields[fromIdx < toIdx ? toIdx : toIdx - 1];
          const cardFieldAfter = cardFields[fromIdx < toIdx ? toIdx + 1 : toIdx];

          newLexoRank = LexoRank.parse(cardFieldAfter.lexorank ?? '')
            .between(LexoRank.parse(cardFieldBefore.lexorank ?? ''))
            .format();
        }
      }

      const updatedCardFields = cardFields.map((field) => {
        if (field.id === cardFieldId) {
          return {
            ...field,
            lexorank: newLexoRank,
          };
        }
        return field;
      });

      const sortedCardFields = updatedCardFields.sort((a, b) => {
        return a.lexorank?.localeCompare(b.lexorank ?? '') ?? 0;
      });

      const cachedCardId = apolloClient.cache.identify({
        id: card?.id,
        __typename: 'Card',
      });

      if (card?.id) {
        apolloClient.cache.writeFragment({
          id: cachedCardId,
          fragment: CardDetailsFragmentDoc,
          fragmentName: 'CardDetails',
          data: {
            ...card,
            id: card.id,
            fields: sortedCardFields,
          },
        });
      }

      await updateCardField({
        variables: {
          input: {
            card_id: card?.id,
            id: cardFieldId,
            lexorank: newLexoRank,
          },
        },
      });

      ctx.unlock({
        command: 'activate-last-request',
      });
    },
    [card, ctx, updateCardField],
  );

  return {
    handleDragEnd,
    handleDragStart,
  };
}
